import {
  Contribution,
  ContributionClassesAndRolesDDL,
  ContributionClassName,
  ContributionClassRole,
  ContributionClassRolesDDL,
} from 'types/contribution';
import { ProfileItem, ProfileType } from 'types/profile';
import { SearchableProfile } from 'types/notification.types';
import {
  ContributionFormData,
  GetDefaultValuesProp,
  PerformerRoleType,
} from './types';

export const countryToOption = (country?: { key: string; name: string }) =>
  country
    ? {
        title: country.name,
        value: country.key,
      }
    : undefined;

const findRole = (roles: ContributionClassRolesDDL, key?: string) =>
  Object.values(roles).find((el) => el.key === key);

const toOptionOrNull = (role?: ContributionClassRole) =>
  role ? { value: role.key, title: role.name } : null;

const getPerformerRoleDetail = (
  contributionClassesAndRoles: ContributionClassesAndRolesDDL,
  contribution?: Contribution
) => {
  if (contribution?.className !== ContributionClassName.PERFORMER) {
    return null;
  }
  if (
    contribution.role === PerformerRoleType.VOCAL ||
    contribution.role === PerformerRoleType.VOCALIST
  ) {
    const roleDetail = findRole(
      contributionClassesAndRoles?.performanceVocals,
      contribution?.roleDetail
    );
    return toOptionOrNull(roleDetail);
  }
  if (contribution.role === PerformerRoleType.MUSICIAN) {
    const roleDetail = findRole(
      contributionClassesAndRoles?.performanceInstruments,
      contribution?.roleDetail
    );
    return toOptionOrNull(roleDetail);
  }
  if (
    contribution.role === PerformerRoleType.GROUP ||
    contribution.role === PerformerRoleType.ENSEMBLE
  ) {
    const roleDetail = findRole(
      contributionClassesAndRoles?.performanceGroups,
      contribution?.roleDetail
    );
    return toOptionOrNull(roleDetail);
  }
  return null;
};

const getRoles = (
  contributionClassesAndRoles: ContributionClassesAndRolesDDL,
  contribution?: Contribution
) => {
  let artistRole: ContributionClassRole | undefined;
  let composerRole: ContributionClassRole | undefined;
  let productionRole: ContributionClassRole | undefined;
  let performerRole: ContributionClassRole | undefined;

  if (contribution?.className === ContributionClassName.ARTIST) {
    artistRole = findRole(
      contributionClassesAndRoles.artistRoles,
      contribution.role
    );
  }
  if (contribution?.className === ContributionClassName.SONGWRITER) {
    composerRole = findRole(
      contributionClassesAndRoles.compositionRoles,
      contribution.role
    );
  }
  if (contribution?.className === ContributionClassName.PRODUCTION) {
    productionRole = findRole(
      contributionClassesAndRoles.productionRoles,
      contribution.role
    );
  }
  if (contribution?.className === ContributionClassName.PERFORMER) {
    performerRole = findRole(
      contributionClassesAndRoles.performanceRoles,
      contribution.role
    );
  }

  return {
    artistRole: toOptionOrNull(artistRole),
    composerRole: toOptionOrNull(composerRole),
    productionRole: toOptionOrNull(productionRole),
    performerRole: toOptionOrNull(performerRole),
  };
};

export const getDefaultValues: GetDefaultValuesProp = ({
  contribution,
  countries,
  contributionClassesAndRoles,
}) => {
  const roles = getRoles(contributionClassesAndRoles, contribution);
  const performerRoleDetail = getPerformerRoleDetail(
    contributionClassesAndRoles,
    contribution
  );
  return {
    name: contribution?.profile?.displayName || '',
    contributor: contribution
      ? {
          displayName: contribution.profile?.displayName || '',
          initials: contribution.profile?.initials || '',
          guid: contribution.profile?.guid || '',
          subject: contribution.profile?.subject || '',
          type: ProfileType.LEGAL,
          profileImages: contribution?.profile?.profileImages,
          avatarColors: contribution.profile?.avatarColors,
        }
      : null,
    className: contribution?.className,
    date: {
      date: contribution?.date?.date
        ? new Date(contribution?.date?.date)
        : null,
    },
    location: {
      address: contribution?.location?.address || '',
      name: contribution?.location?.name || '',
      country:
        countryToOption(
          countries[contribution?.location?.country.countryCode || '']
        ) || null,
    },
    artistIpn: contribution?.ipn?.toString() || '',
    artistIsni: contribution?.isni?.toString() || '',
    artistRole: roles.artistRole,
    composerIpi: contribution?.ipi?.toString() || '',
    composerIsni: contribution?.isni?.toString() || '',
    composerRole: roles.composerRole,
    producerIpn: contribution?.ipn?.toString() || '',
    producerIsni: contribution?.isni?.toString() || '',
    productionRole: roles.productionRole,
    performerIpn: contribution?.ipn?.toString() || '',
    performerIsni: contribution?.isni?.toString() || '',
    performerRole: roles.performerRole,
    performerRoleDetail,
    otherRole: contribution?.role || '',
    detail: '',
    profileGuid: '',
  };
};

export const getPersonalSearchableProfile = (
  profile: ProfileItem
): SearchableProfile => ({
  guid: profile.guid,
  displayName: profile.displayName,
  type: profile.type,
  subject: profile.subject,
  initials: profile.initials,
  profileImages: profile.profileImages,
  address: profile.address,
  preferredIpn: profile.preferredIpn,
  preferredIpi: profile.preferredIpi,
  preferredIsni: profile.preferredIsni,
  avatarColors: profile.avatarColors,
  artistName: profile.artistName,
});

export const getDirtyFieldsWithAdditionalInfo = (
  newDirtyFields: Partial<ContributionFormData>,
  data: ContributionFormData
) => {
  const { contributor, ...contributorData } = data;
  const dirtyFieldsWithAdditionalInfo: Partial<ContributionFormData> = {
    ...newDirtyFields,
    ...(contributorData.date?.date
      ? { date: { date: contributorData.date?.date } }
      : {}),
    ...(contributorData?.location?.country
      ? {
          location: {
            ...newDirtyFields.location,
            country: contributorData?.location.country,
          },
        }
      : {}),
  };
  return dirtyFieldsWithAdditionalInfo;
};
